// The menu filter's rules on a restaurant's page (UC-31), apart from the
// page: how a word in an item's name is matched, and which categories are
// one tap away. The worker writes each item's folded name on the page, the
// island folds what the diner types, and these decide what shows.

/** The filter's resting category: every category. */
export const ALL = '';

/** How many categories, in the owner's order, are one tap away beside All categories. */
export const QUICK_CATEGORIES = 4;

/** From this many letters on, a word also matches with one letter wrong, missing or extra. */
export const TOLERANT_FROM = 5;

// The Arabic marks a diner may type or leave out (the harakat, the
// superscript alef and the Quranic marks), and tatweel, which only stretches
// a word.
const MARKS = /[ً-ٰٟۖ-ۭـ]/g;
const ALEFS = /[آأإٱ]/g;

/**
 * A name or a word in the one spelling the match compares: the hamza forms
 * of alef as alef, ى as ي, ة as ه, no marks or tatweel, Latin letters in
 * lower case, and one space between words.
 */
export function fold(text: string): string {
	return text
		.replace(MARKS, '')
		.replace(ALEFS, 'ا')
		.replace(/ى/g, 'ي')
		.replace(/ة/g, 'ه')
		.toLowerCase()
		.replace(/\s+/g, ' ')
		.trim();
}

/**
 * Whether a folded word is part of a folded name. An empty word matches
 * every name. A word of TOLERANT_FROM letters or more also matches a part of
 * the name one edit away, so «اسبريسو» finds «اسبرسو».
 */
export function matches(name: string, word: string): boolean {
	if (name.includes(word)) return true;
	const letters = [...word];
	if (letters.filter((c) => c !== ' ').length < TOLERANT_FROM) return false;
	return editsToPart([...name], letters) <= 1;
}

// The fewest edits that turn the word into some part of the name: an edit
// distance whose start and end in the name are free.
function editsToPart(name: string[], word: string[]): number {
	let row = new Array<number>(name.length + 1).fill(0);
	for (let i = 1; i <= word.length; i++) {
		const next = [i];
		for (let j = 1; j <= name.length; j++) {
			const swap = row[j - 1]! + (word[i - 1] === name[j - 1] ? 0 : 1);
			next.push(Math.min(swap, row[j]! + 1, next[j - 1]! + 1));
		}
		row = next;
	}
	return Math.min(...row);
}

/**
 * The categories one tap away after All categories: the first
 * QUICK_CATEGORIES in the owner's order, then the picked one when it is not
 * among them.
 */
export function quickCategories(categories: string[], picked: string): string[] {
	const quick = categories.slice(0, QUICK_CATEGORIES);
	return picked !== ALL && !quick.includes(picked) && categories.includes(picked)
		? [...quick, picked]
		: quick;
}

/**
 * Whether a filter that matches nothing also offers to look for its word in
 * every category: only when both a category and a word are set.
 */
export function offersEveryCategory(category: string, word: string): boolean {
	return category !== ALL && word !== '';
}
